import { useCallback, useState } from 'react'

export interface Action {
  busy: boolean
  error: unknown
  /** Executa a escrita. Falha fica em `error`; sucesso é responsabilidade da tarefa. */
  run: (task: () => Promise<void>) => void
  reset: () => void
}

/**
 * Uma escrita em voo.
 *
 * Não existe retry automático em lugar nenhum, e isso é deliberado: os erros
 * desta API que mais aparecem são `409` — conflito com o estado atual. Repetir
 * a mesma requisição devolve o mesmo conflito; o que precisa mudar é a intenção
 * do operador, e para isso ele tem que ler o motivo.
 *
 * `busy` também é o guarda contra o clique duplo, que numa tela de revogação
 * não pode virar duas requisições.
 */
export function useAction(): Action {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const run = useCallback(
    (task: () => Promise<void>) => {
      if (busy) return

      setBusy(true)
      setError(null)

      task()
        .catch((cause: unknown) => setError(cause))
        .finally(() => setBusy(false))
    },
    [busy],
  )

  const reset = useCallback(() => setError(null), [])

  return { busy, error, run, reset }
}
